"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";
import type { PageTexts } from "@/lib/siteTexts";

// Wording comes from Admin -> Тексты страниц (virtual-machines.*).
const featureStyles = [
  { icon: "BoltIcon", color: "#00D4FF" },
  { icon: "CameraIcon", color: "#4F7CFF" },
  { icon: "ArrowsPointingOutIcon", color: "#7C3AED" },
  { icon: "CodeBracketIcon", color: "#00FF88" },
  { icon: "ShieldCheckIcon", color: "#FF6B6B" },
  { icon: "ClockIcon", color: "#FFB800" },
] as const;

const NUMBERS = [1, 2, 3, 4, 5, 6] as const;

export default function VMFeatures({ texts: t }: { texts: PageTexts<"virtual-machines"> }) {
  const features = featureStyles.map((style, i) => ({
    ...style,
    title: t[`features.item${NUMBERS[i]}.title`],
    description: t[`features.item${NUMBERS[i]}.text`],
  }));
  const networkFeatures = NUMBERS.map((n) => ({
    name: t[`network.item${n}.name`],
    desc: t[`network.item${n}.text`],
  }));
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-[#0D1526] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Features */}
        <div className="text-center mb-16 reveal">
          <span className="section-label">{t["features.label"]}</span>
          <h2 className="font-display font-bold text-4xl text-white mt-3 mb-4">
            {t["features.title"]}{" "}
            <span className="gradient-text">{t["features.titleAccent"]}</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {features.map((f, idx) => (
            <div
              key={f.icon}
              className="glass rounded-2xl p-7 card-hover reveal group"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                style={{ background: `${f.color}15` }}
              >
                <Icon name={f.icon as any} size={24} style={{ color: f.color }} />
              </div>
              <h3 className="font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-[#8BA3CC] text-sm leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>

        {/* Network section */}
        <div className="glass rounded-3xl p-10 neon-border reveal">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="section-label">{t["network.label"]}</span>
              <h3 className="font-display font-bold text-3xl text-white mt-3 mb-4">
                {t["network.title"]}{" "}
                <span className="gradient-text">{t["network.titleAccent"]}</span>
              </h3>
              <p className="text-[#8BA3CC] leading-relaxed mb-6">
                {t["network.subtitle"]}
              </p>

              {/* Visual network diagram */}
              <div className="relative w-full h-40 flex items-center justify-center">
                <div className="absolute w-20 h-20 rounded-full glass neon-border flex items-center justify-center text-[#00D4FF] text-xs font-bold">
                  {t["network.diagram.center"]}
                </div>
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 160">
                  <line x1="200" y1="80" x2="80" y2="40" stroke="rgba(0,212,255,0.3)" strokeWidth="1" strokeDasharray="4,4" />
                  <line x1="200" y1="80" x2="200" y2="20" stroke="rgba(0,212,255,0.3)" strokeWidth="1" strokeDasharray="4,4" />
                  <line x1="200" y1="80" x2="320" y2="40" stroke="rgba(0,212,255,0.3)" strokeWidth="1" strokeDasharray="4,4" />
                  <circle cx="80" cy="40" r="18" fill="rgba(79,124,255,0.15)" stroke="rgba(79,124,255,0.4)" strokeWidth="1" />
                  <text x="80" y="44" textAnchor="middle" fill="#4F7CFF" fontSize="8" fontWeight="bold">{t["network.diagram.node1"]}</text>
                  <circle cx="200" cy="20" r="18" fill="rgba(124,58,237,0.15)" stroke="rgba(124,58,237,0.4)" strokeWidth="1" />
                  <text x="200" y="24" textAnchor="middle" fill="#7C3AED" fontSize="8" fontWeight="bold">{t["network.diagram.node2"]}</text>
                  <circle cx="320" cy="40" r="18" fill="rgba(0,255,136,0.15)" stroke="rgba(0,255,136,0.4)" strokeWidth="1" />
                  <text x="320" y="44" textAnchor="middle" fill="#00FF88" fontSize="8" fontWeight="bold">{t["network.diagram.node3"]}</text>
                </svg>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {networkFeatures.map((nf, i) => (
                <div
                  key={i}
                  className="p-4 rounded-xl border border-[rgba(79,124,255,0.12)] hover:border-[rgba(0,212,255,0.3)] hover:bg-[rgba(0,212,255,0.04)] transition-all"
                >
                  <div className="text-[#00D4FF] font-bold text-sm mb-1">{nf.name}</div>
                  <div className="text-[#4A6080] text-xs leading-relaxed">{nf.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}